import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/** Admin-only tab inside the PH360 workspace.
 *  Two sections:
 *    1. Registered Users manager — toggle role admin↔user and free-access
 *    2. Free-Access Allowlist by email (also covers unregistered addresses)
 */
export default function TabAdmin() {
  const [tab, setTab] = useState("users");
  return (
    <div data-testid="tab-admin">
      <h2>Admin — Owner Settings</h2>
      <div className="ef360-subtabs" style={{display:"flex", gap:12, marginBottom:12, borderBottom:"1px solid #dcdcde"}}>
        {[
          ["users", "Registered users"],
          ["allowlist", "Allowlist (by email)"],
          ["security", "Security status"],
        ].map(([k, label]) => (
          <button key={k} type="button" onClick={()=>setTab(k)}
            data-testid={`admin-subtab-${k}`}
            style={{
              background:"transparent", border:"none",
              borderBottom: tab === k ? "3px solid #2271b1" : "3px solid transparent",
              fontWeight: tab === k ? 600 : 400, cursor:"pointer",
              padding:"6px 4px", fontSize:14,
            }}>{label}</button>
        ))}
      </div>
      {tab === "users" && <UsersManager />}
      {tab === "allowlist" && <Allowlist />}
      {tab === "security" && <SecurityStatus />}
    </div>
  );
}


/** Iter-47 — live view of the login limiter. Auto-refreshes every 15 s so
 *  operators can watch credential-stuffing attempts in real time. */
function SecurityStatus() {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/security-status");
      setSnap(data);
      setErr(null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  if (err) return <div data-testid="security-status-error" style={{color:"#a00"}}>{err}</div>;
  if (!snap) return <div>Loading…</div>;

  const cfg = snap.config || {};
  const sizes = snap.map_sizes || {};

  return (
    <div data-testid="security-status">
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:12}}>
        <div style={{fontSize:12, color:"#646970"}}>
          Snapshot at {new Date(snap.generated_at).toLocaleTimeString()}
          {busy ? " · refreshing…" : ""}
        </div>
        <button type="button" onClick={load} data-testid="security-refresh" className="button">Refresh</button>
      </div>

      {/* Config summary */}
      <div style={{background:"#f6f7f7", padding:12, marginBottom:16, fontSize:13}}>
        <strong>Limiter config</strong>
        <div style={{display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:8, marginTop:8}}>
          <div><span style={{color:"#646970"}}>Window:</span> {cfg.window_minutes} min</div>
          <div><span style={{color:"#646970"}}>Max fails / IP:</span> {cfg.max_fails_per_ip}</div>
          <div><span style={{color:"#646970"}}>Email slowdown:</span> {cfg.slowdown_seconds}s</div>
          <div><span style={{color:"#646970"}}>Map cap:</span> {cfg.map_key_cap?.toLocaleString?.() || cfg.map_key_cap}</div>
          <div><span style={{color:"#646970"}}>Trusted proxies:</span> {cfg.trusted_proxies_configured ? "yes" : "no (permissive)"}</div>
          <div><span style={{color:"#646970"}}>API docs exposed:</span> {cfg.docs_enabled ? "YES ⚠" : "no"}</div>
          <div><span style={{color:"#646970"}}>IPs tracked:</span> {sizes.unique_ips_tracked}</div>
          <div><span style={{color:"#646970"}}>Emails tracked:</span> {sizes.unique_emails_tracked}</div>
        </div>
      </div>

      {/* Two side-by-side tables */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
        <RankTable
          testid="security-top-ips"
          title="Top offending IPs (last window)"
          headerLabel="IP"
          rows={snap.top_offending_ips}
          emptyMsg="No IPs with active failures. All quiet."
        />
        <RankTable
          testid="security-top-emails"
          title="Top targeted emails (last window)"
          headerLabel="Email"
          rows={snap.top_targeted_emails}
          emptyMsg="No emails under attack. All quiet."
        />
      </div>
    </div>
  );
}


function RankTable({ testid, title, headerLabel, rows, emptyMsg }) {
  return (
    <div data-testid={testid}>
      <strong style={{fontSize:13}}>{title}</strong>
      {rows?.length ? (
        <table className="wp-list-table widefat striped" style={{marginTop:8}}>
          <thead>
            <tr>
              <th>{headerLabel}</th>
              <th style={{width:80}}>Failures</th>
              <th style={{width:160}}>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.key}-${i}`} data-testid={`${testid}-row-${i}`}>
                <td style={{fontFamily:"monospace", fontSize:12, wordBreak:"break-all"}}>{r.key}</td>
                <td>{r.count}</td>
                <td style={{fontSize:12, color:"#646970"}}>
                  {r.last_seen ? new Date(r.last_seen).toLocaleTimeString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div style={{padding:12, background:"#f6f7f7", fontSize:12, color:"#646970", marginTop:8}}>{emptyMsg}</div>
      )}
    </div>
  );
}


function UsersManager() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setRole = async (u, role) => {
    if (u.role === role) return;
    if (role === "user" && u.id === me?.id) {
      toast.error("You can't remove your own admin role. Ask another admin.");
      return;
    }
    if (!window.confirm(`${role === "admin" ? "Promote" : "Demote"} ${u.email} to ${role}?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`Role updated for ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const setFree = async (u, enabled) => {
    setBusyId(u.id);
    try {
      const note = enabled
        ? (window.prompt(`Note for ${u.email}? (optional)`, u.free_note || "") ?? "")
        : "";
      if (enabled && note === null) { setBusyId(null); return; } // cancelled
      await api.post(`/admin/users/${u.id}/free-access`, { enabled, note });
      toast.success(`${enabled ? "Granted" : "Revoked"} free access for ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const removeUser = async (u) => {
    if (u.id === me?.id) { toast.error("You can't delete your own account."); return; }
    if (u.role === "admin") {
      toast.error("Demote this admin to a user first, then delete.");
      return;
    }
    const proj = u.project_count || 0;
    const warn = proj > 0
      ? `This will permanently delete ${u.email} and their ${proj} project(s), books, media, and unlocks. Continue?`
      : `Permanently delete ${u.email}? This cannot be undone.`;
    if (!window.confirm(warn)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-users-section">
      <p className="description">
        Every registered user. <strong>Admins</strong> and <strong>free-access</strong> users bypass Stripe
        and never see the unlock button on the Preview tab.
      </p>
      <p style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
        <input type="search" placeholder="Search email or name…" value={q}
          onChange={(e)=>setQ(e.target.value)} data-testid="admin-users-search"
          style={{width:260, marginTop:4}} />
        <button className="button button-primary" type="button"
          onClick={()=>setShowCreate(true)} data-testid="admin-open-create-user"
          style={{marginLeft:"auto"}}>
          + Add user (email + password)
        </button>
      </p>
      {showCreate && (
        <CreateUserModal onClose={()=>setShowCreate(false)} onCreated={load}/>
      )}
      {rows === null && <p className="description">Loading…</p>}
      {rows && rows.length === 0 && <p className="description">No users match.</p>}
      {rows && rows.length > 0 && (
        <table className="wp-list-table widefat striped" data-testid="admin-users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Access</th>
              <th style={{width:130}}>Projects</th>
              <th style={{width:360}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} data-testid={`admin-user-row-${u.email}`}>
                  <td>
                    <div style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{u.email}{isMe && <span style={{marginLeft:6, fontSize:10, color:"#8c8f94"}}>(you)</span>}</div>
                    {u.name && <div style={{fontSize:11, color:"#5c5c58"}}>{u.name}</div>}
                    <div style={{fontSize:10, color:"#8c8f94"}}>{(u.created_at || "").slice(0, 10)}</div>
                  </td>
                  <td>
                    <span style={{display:"inline-flex", gap:4, flexWrap:"wrap"}}>
                      <Pill label={u.role === "admin" ? "ADMIN" : "USER"} color={u.role === "admin" ? "#2271b1" : "#8c8f94"} />
                      {u.is_free_user && <Pill label="FREE" color="#00a32a" />}
                      {u.has_subscription && <Pill label={`SUB: ${u.subscription_plan}`} color="#7f54b3" />}
                    </span>
                    {u.is_free_user && u.free_note && (
                      <div style={{fontSize:11, color:"#5c5c58", marginTop:4}}>Note: {u.free_note}</div>
                    )}
                  </td>
                  <td>{u.project_count}</td>
                  <td>
                    <div style={{display:"flex", gap:4, flexWrap:"wrap"}}>
                      {u.role === "admin" ? (
                        <button className="button" onClick={()=>setRole(u, "user")} disabled={busyId === u.id || isMe}
                          data-testid={`admin-demote-${u.email}`} style={{fontSize:11, padding:"2px 8px"}}
                          title={isMe ? "You can't demote yourself" : ""}>Demote to user</button>
                      ) : (
                        <button className="button" onClick={()=>setRole(u, "admin")} disabled={busyId === u.id}
                          data-testid={`admin-promote-${u.email}`} style={{fontSize:11, padding:"2px 8px"}}>Make admin</button>
                      )}
                      {u.is_free_user ? (
                        <button className="button" onClick={()=>setFree(u, false)} disabled={busyId === u.id}
                          data-testid={`admin-revoke-free-${u.email}`} style={{fontSize:11, padding:"2px 8px"}}>Revoke free</button>
                      ) : (
                        <button className="button button-primary" onClick={()=>setFree(u, true)} disabled={busyId === u.id}
                          data-testid={`admin-grant-free-${u.email}`} style={{fontSize:11, padding:"2px 8px"}}>Grant free</button>
                      )}
                      <button className="button delete" onClick={()=>removeUser(u)}
                        disabled={busyId === u.id || isMe || u.role === "admin"}
                        data-testid={`admin-delete-${u.email}`}
                        title={isMe ? "You can't delete yourself" : (u.role === "admin" ? "Demote first" : "")}
                        style={{fontSize:11, padding:"2px 8px", color:"#b32d2e", borderColor:"#b32d2e"}}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/admin/users", { email, password, name: name || null, role });
      toast.success(`Created ${email}`);
      onCreated?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-create-user-modal"
      style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:100,
              display:"flex", alignItems:"center", justifyContent:"center"}}
      onClick={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} style={{background:"#fff", padding:24, width:420,
        border:"1px solid #dcdcde", maxWidth:"90vw"}}>
        <h3 style={{marginTop:0, marginBottom:4}}>Add a new user</h3>
        <p className="description" style={{marginTop:0, marginBottom:16}}>
          Creates a real login account with the email + password you set below.
          The user can sign in immediately.
        </p>
        <label>Email <span style={{color:"#b32d2e"}}>*</span></label>
        <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
          placeholder="user@example.com" data-testid="admin-create-email"
          style={{width:"100%"}} />
        <label style={{marginTop:10}}>Password (min 6) <span style={{color:"#b32d2e"}}>*</span></label>
        <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)}
          data-testid="admin-create-password"
          style={{width:"100%"}} />
        <label style={{marginTop:10}}>Name (optional)</label>
        <input type="text" value={name} onChange={(e)=>setName(e.target.value)}
          placeholder="Jane Doe" data-testid="admin-create-name"
          style={{width:"100%"}} />
        <label style={{marginTop:10}}>Role</label>
        <select value={role} onChange={(e)=>setRole(e.target.value)}
          data-testid="admin-create-role" style={{width:"100%"}}>
          <option value="user">Regular user</option>
          <option value="admin">Admin</option>
        </select>
        <div style={{marginTop:20, display:"flex", gap:8, justifyContent:"flex-end"}}>
          <button type="button" className="button" onClick={onClose} disabled={busy}
            data-testid="admin-create-cancel">Cancel</button>
          <button type="submit" className="button button-primary" disabled={busy}
            data-testid="admin-create-submit">
            {busy ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Pill({ label, color }) {
  return (
    <span style={{
      background: color, color:"#fff", fontSize:10, padding:"2px 6px",
      borderRadius:3, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5,
    }}>{label}</span>
  );
}

function Allowlist() {
  const [items, setItems] = useState(null);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/free-users");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const list = emails.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!list.length) { toast.error("Enter at least one email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/free-users/bulk", { emails: list, note });
      toast.success(`Added / updated ${data.added} email(s)`);
      setEmails(""); setNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke free-access for ${email}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/free-users/${encodeURIComponent(email)}`);
      toast.success("Revoked");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const rows = (items || []).filter(x => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (x.email || "").toLowerCase().includes(f) || (x.note || "").toLowerCase().includes(f);
  });
  const active = rows.filter(x => x.is_active);
  const revoked = rows.filter(x => !x.is_active);

  return (
    <div>
      <p className="description">
        Add emails here to grant free access even before someone registers — great
        for beta testers you invite by email.
      </p>
      <form onSubmit={add} className="ef360-card-inner" data-testid="admin-add-form"
        style={{padding:14, background:"#fff", border:"1px solid #dcdcde", marginTop:12}}>
        <label>Emails (one per line, or comma-separated)</label>
        <textarea rows={3} value={emails} onChange={(e)=>setEmails(e.target.value)}
          placeholder="richard@example.com, mary@example.com&#10;team@studio.com"
          data-testid="admin-emails-input"
          style={{width:"100%", fontFamily:"ui-monospace, SFMono-Regular, monospace"}}/>
        <label style={{marginTop:8}}>Note (optional)</label>
        <input type="text" value={note} onChange={(e)=>setNote(e.target.value)}
          placeholder="Founding member, maintainer, tester…" data-testid="admin-note-input"/>
        <p style={{marginTop:10}}>
          <button className="button button-primary" type="submit" disabled={busy} data-testid="admin-add-submit">
            {busy ? "Saving…" : "Add / update"}
          </button>
        </p>
      </form>

      <div style={{marginTop:18, display:"flex", alignItems:"center", justifyContent:"space-between"}}>
        <h3 style={{margin:0}}>Active — {active.length}</h3>
        <input type="search" placeholder="Filter…" value={filter} onChange={(e)=>setFilter(e.target.value)}
          data-testid="admin-filter" style={{width:220}} />
      </div>
      {items === null && <p className="description">Loading…</p>}
      {items && active.length === 0 && <p className="description">No emails match.</p>}
      {active.length > 0 && (
        <table className="wp-list-table widefat striped" data-testid="admin-active-table">
          <thead>
            <tr><th>Email</th><th>Note</th><th>Status</th><th>Added</th><th style={{width:100}}></th></tr>
          </thead>
          <tbody>
            {active.map(x => (
              <tr key={x.email} data-testid={`admin-row-${x.email}`}>
                <td style={{fontFamily:"ui-monospace, SFMono-Regular, monospace"}}>{x.email}</td>
                <td>{x.note || <span style={{color:"#8c8f94"}}>—</span>}</td>
                <td>
                  <span style={{display:"inline-flex", gap:4, flexWrap:"wrap"}}>
                    <Pill label={x.is_registered ? "REGISTERED" : "NOT YET REGISTERED"}
                          color={x.is_registered ? "#00a32a" : "#8c8f94"} />
                    {x.role === "admin" && <Pill label="ADMIN" color="#2271b1" />}
                    {x.has_subscription && <Pill label={`SUB: ${x.subscription_plan}`} color="#7f54b3" />}
                  </span>
                </td>
                <td style={{fontSize:12, color:"#5c5c58"}}>{(x.added_at || "").slice(0,10)}</td>
                <td>
                  <button className="button delete" onClick={()=>revoke(x.email)} disabled={busy}
                    data-testid={`admin-revoke-${x.email}`} style={{fontSize:12, padding:"2px 8px"}}>Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revoked.length > 0 && (
        <>
          <h3 style={{marginTop:24, color:"#8c8f94"}}>Revoked — {revoked.length}</h3>
          <ul style={{color:"#8c8f94", fontFamily:"ui-monospace, monospace", fontSize:12}}>
            {revoked.map(x => <li key={x.email}>{x.email}{x.note ? ` — ${x.note}` : ""}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}
